/**
 * Vue.js Application Entry Point
 * Main application bootstrap file with clean separation of concerns
 */
import { createApp } from 'vue'
import AppComponent from './App.vue'
import router from './router'
import { createPinia } from 'pinia'

// Styles - Import order matters for cascading
import './assets/construction-app.css' // Main comprehensive stylesheet
import './assets/styles.css' // Legacy styles for backward compatibility
import './assets/design-system.css' // Design system utilities
import './assets/modern-reports.css' // Report-specific styles
import './assets/mobile-optimizations.css' // Mobile optimizations
import './assets/rtl.css' // RTL language support
import '@fortawesome/fontawesome-free/css/all.css'
import 'primeicons/primeicons.css'
import './assets/primevue-theme.css'

// Third-party libraries
import PrimeVue from 'primevue/config'
import Galleria from 'primevue/galleria'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import ganttastic from '@infectoone/vue-ganttastic'

// AG Grid modules registration
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
ModuleRegistry.registerModules([ AllCommunityModule ]);

// Application configuration modules
import { setupAxios } from './config/axios'
import { createI18nInstance } from './config/i18n'
import { initializePiniaApp, setupPiniaSessionManager } from './config/app'
import configService from './services/config.service'

// Plugins
import notificationsPlugin from './plugins/notifications'
import archiWebAuthService from './services/archiweb.auth.service'
import { initializeGlobalNotifications } from './utils/globalNotifications'

// Type definitions
import type { AppConfig } from './types/config'

/**
 * Application Bootstrap Function
 */
async function bootstrap(): Promise<void> {
  try {
    // Load configuration first
    const config: AppConfig = await configService.getConfig()
    
    // Initialize authentication service with configuration
    await archiWebAuthService.initialize()
    console.log('✅ Authentication service initialized with configuration')
    
    // Configure Vue DevTools based on configuration
    if (typeof window !== 'undefined') {
      window.__VUE_PROD_DEVTOOLS__ = config.ENABLE_DEVTOOLS
    }
    
    // Create Pinia store
    const pinia = createPinia()
    
    // Setup axios configuration
    setupAxios()
    
    // Create i18n instance
    const i18n = createI18nInstance()
    
    // Create Vue app instance
    const app = createApp(AppComponent)
    
    // Install plugins
    app.use(router)
    app.use(pinia)
    app.use(i18n)
    app.use(PrimeVue)
    app.use(ConfirmationService)
    app.use(ToastService)
    app.use(ganttastic)
    app.use(notificationsPlugin)
    
    // Initialize global notifications
    initializeGlobalNotifications()
    
    // Register components
    app.component('PGalleria', Galleria)
    
    // Make i18n globally available for reactivity
    if (typeof window !== 'undefined') {
      window.i18n = i18n
    }
    
    // Initialize application
    await initializePiniaApp(i18n)
    
    // Setup session manager watcher
    setupPiniaSessionManager()
    
    // Mount the application
    app.mount('#app')
    
    console.log('✅ Application successfully bootstrapped')
  } catch (error) {
    console.error('❌ Failed to bootstrap application:', error)
    throw error
  }
}

// Start the application
bootstrap().catch((error) => {
  console.error('❌ Critical error during application startup:', error)
})
